import React from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  Fish,
  Calendar,
  MapPin,
  Clock,
  Users,
  ArrowRight,
  Waves,
  Leaf,
  ShoppingBag,
} from "lucide-react";
import { Button } from "../components/ui/button";
import SectionTitle from "../components/SectionTitle";
import { club, images, stats, waters, events, yearsOfTradition, announcement, isAnnouncementVisible, getActiveWaters } from "../mock/mock";
import { upcoming, monthShort } from "../lib/dateUtils";

const highlights = [
  {
    icon: Waves,
    title: "Unsere Gewässer",
    text: "Kanäle, Flüsse und Weiher rund um Fürth mit vielfältigem Fischbestand.",
    to: "/gewaesser",
  },
  {
    icon: Calendar,
    title: "Termine 2026",
    text: "Vereinsheimöffnungen, Königsfischen, Feste und Veranstaltungen.",
    to: "/termine",
  },
  {
    icon: ShoppingBag,
    title: "Gastkarten",
    text: "Tageskarten online über hejfish oder an unseren Verkaufsstellen.",
    to: "/gastkarten",
  },
  {
    icon: Leaf,
    title: "Jugend & Natur",
    text: "Wir begeistern junge Menschen fürs Angeln und den Gewässerschutz.",
    to: "/jugend",
  },
];

export default function Home() {
  const nextEvents = upcoming(
    events.filter((e) => e.type === "Veranstaltung")
  ).slice(0, 5);
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.heroLakeSunrise})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-primary/80" />

        <div className="relative flex-1 flex items-center w-full">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 w-full pt-32 pb-14">
            <div className="max-w-3xl fade-up">
              <div className="mb-7 inline-flex items-center justify-center h-28 w-28 md:h-40 md:w-40 rounded-full bg-white/95 shadow-2xl ring-4 ring-white/30 p-4">
                <img
                  src={club.logo}
                  alt="Fischerei Verein Fürth e.V. – Logo"
                  className="h-full w-full object-contain"
                />
              </div>
              <span className="flex items-center gap-2 text-accent text-sm font-600 uppercase tracking-[0.3em] mb-5">
                <Fish className="h-4 w-4" /> gegründet 1881
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-600 text-white leading-[0.95]">
                Fischerei Verein
                <br />
                <span className="text-accent">Fürth e.V.</span>
              </h1>
              <p className="mt-6 text-lg md:text-2xl text-white/90 font-300 max-w-2xl leading-relaxed">
                Ein traditionsreicher Angelverein im Herzen Fürths – mit eigenen
                Gewässern, lebendiger Gemeinschaft und Leidenschaft für die Natur.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a href={club.hejfish} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-600 text-base h-13 px-7 gap-2"
                  >
                    Angelkarten online kaufen
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
                <Link to="/verein">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white font-500 text-base h-13 px-7 backdrop-blur-sm"
                  >
                    Verein kennenlernen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar – im normalen Fluss, überlappt keine Inhalte mehr */}
        <div className="relative w-full">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="bg-background/95 backdrop-blur-md rounded-t-2xl grid grid-cols-2 md:grid-cols-4 divide-x divide-border border-t border-x border-border">
              {stats.map((s) => (
                <div key={s.label} className="px-6 py-6 text-center">
                  <div className="font-serif text-3xl md:text-4xl font-700 text-primary">
                    {s.label === "Gewässer" ? getActiveWaters().length : s.value}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AKTUELLE ANKÜNDIGUNG – verschwindet automatisch nach dem Event */}
      {isAnnouncementVisible() && (
        <section className="py-16 bg-secondary/60 border-b border-border" data-testid="announcement-banner">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="bg-card rounded-3xl border border-border shadow-xl overflow-hidden grid md:grid-cols-2">
              <div className="bg-secondary flex items-center justify-center p-6">
                <a href={announcement.pdf} target="_blank" rel="noopener noreferrer">
                  <img
                    src={announcement.image}
                    alt={`Flyer ${announcement.title}`}
                    className="max-h-[420px] w-auto rounded-xl shadow-lg hover:scale-[1.02] transition-transform duration-500"
                  />
                </a>
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <span className="inline-flex items-center gap-2 text-accent text-sm font-600 uppercase tracking-[0.25em] mb-4">
                  <Calendar className="h-4 w-4" /> Aktuelle Veranstaltung
                </span>
                <h2 className="font-serif text-3xl md:text-4xl font-600 text-foreground leading-tight">
                  {announcement.title}
                </h2>
                <div className="mt-5 space-y-2 text-foreground/80">
                  <p className="flex items-center gap-2.5">
                    <Calendar className="h-5 w-5 text-primary shrink-0" /> {announcement.dateLabel}
                  </p>
                  <p className="flex items-center gap-2.5">
                    <Clock className="h-5 w-5 text-primary shrink-0" /> {announcement.time}
                  </p>
                  <p className="flex items-start gap-2.5">
                    <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" /> {announcement.location}
                  </p>
                </div>
                <p className="mt-5 text-muted-foreground leading-relaxed">
                  {announcement.text}
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <a href={announcement.pdf} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-600 gap-2" data-testid="announcement-flyer-button">
                      Flyer ansehen (PDF)
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(announcement.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-border text-foreground/80 hover:bg-secondary gap-2">
                      <MapPin className="h-4 w-4" /> Route planen
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* WELCOME */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={images.anglerMisty}
                  alt="Angler am Wasser"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 md:-right-8 bg-primary text-primary-foreground rounded-xl px-7 py-5 shadow-xl">
                <div className="font-serif text-4xl font-700 text-accent">{yearsOfTradition}+</div>
                <div className="text-sm text-primary-foreground/80">
                  Jahre Tradition
                </div>
              </div>
            </div>

            <div>
              <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
                Herzlich Willkommen
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-600 text-foreground leading-tight">
                Petri Heil im Fischerei Verein Fürth
              </h2>
              <div className="mt-6 space-y-4 text-lg text-muted-foreground font-300 leading-relaxed">
                <p>
                  Gegründet 1881, blicken wir auf eine lange und stolze
                  Geschichte zurück. Unser Vereinsheim in der Amtsgasse 2 in
                  Fürth/Vach ist der Treffpunkt für alle Mitglieder – an
                  ausgewählten Montagen geöffnet.
                </p>
                <p>
                  Neu ab 2026: Tageskarten könnt ihr jetzt bequem online über
                  „hejfish“ beziehen. Bei Fragen zu Aufnahme, Beiträgen und mehr
                  helfen wir gerne persönlich weiter.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/verein">
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-500 gap-2">
                    Mehr über uns <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/kontakt">
                  <Button
                    variant="outline"
                    className="border-primary/30 text-primary hover:bg-secondary font-500"
                  >
                    Kontakt aufnehmen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-24 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTitle
            eyebrow="Entdecken"
            title="Alles rund um unseren Verein"
            description="Von unseren Gewässern über Termine bis zur Jugendarbeit – hier findet ihr die wichtigsten Themen auf einen Blick."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h) => {
              const Icon = h.icon;
              return (
                <Link
                  key={h.title}
                  to={h.to}
                  className="group bg-card rounded-2xl p-7 border border-border hover:border-primary/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300">
                    <Icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-serif text-2xl font-600 text-foreground mb-2">
                    {h.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {h.text}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-600 text-primary">
                    Mehr erfahren
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* GEWAESSER PREVIEW */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
                Unsere Reviere
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-600 text-foreground leading-tight">
                Gewässer mit Charakter
              </h2>
            </div>
            <Link to="/gewaesser">
              <Button
                variant="outline"
                className="border-primary/30 text-primary hover:bg-secondary font-500 gap-2"
              >
                Alle Gewässer <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {waters.slice(0, 3).map((w) => (
              <div
                key={w.id}
                className="group relative rounded-2xl overflow-hidden shadow-lg h-80"
              >
                <img
                  src={w.image}
                  alt={w.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block text-xs font-600 uppercase tracking-wider text-accent mb-2">
                    {w.type}
                  </span>
                  <h3 className="font-serif text-2xl font-600 text-white">
                    {w.name}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {w.fish.slice(0, 3).map((f) => (
                      <span
                        key={f}
                        className="text-xs bg-white/15 backdrop-blur-sm text-white px-2.5 py-1 rounded-full"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS PREVIEW */}
      <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.boatSunset})` }}
        />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
                Vereinsleben
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-600 leading-tight">
                Das nächste Vereinsleben
              </h2>
              <p className="mt-5 text-lg text-primary-foreground/80 font-300 leading-relaxed">
                Ob Königsfischen, Damenfischen oder Stadtwaldfest – bei uns ist
                immer etwas los. Feiert und angelt gemeinsam mit uns.
              </p>
              <Link to="/termine" className="inline-block mt-8">
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-600 gap-2">
                  Alle Termine ansehen <Calendar className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {nextEvents.length === 0 ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-6 border border-white/10 text-primary-foreground/85">
                  Die Termine für die neue Saison werden in Kürze veröffentlicht.
                  Schaut bald wieder vorbei!
                </div>
              ) : (
                nextEvents.map((e) => (
                  <div
                    key={e.title}
                    className="flex items-center gap-5 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/10 hover:bg-white/15 transition-colors"
                  >
                    <div className="text-center shrink-0 w-16">
                      <div className="font-serif text-2xl font-700 text-accent leading-none">
                        {e.date.split(".")[0]}
                      </div>
                      <div className="text-xs uppercase text-primary-foreground/70 mt-1">
                        {monthShort(e.date)}
                      </div>
                    </div>
                    <div className="h-10 w-px bg-white/20" />
                    <div className="font-500 text-primary-foreground">{e.title}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* JUGEND CTA */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 rounded-3xl overflow-hidden shadow-2xl border border-border">
            <div className="relative min-h-[320px]">
              <img
                src={images.kidToddler}
                alt="Jugendarbeit"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="bg-secondary/60 p-10 lg:p-14 flex flex-col justify-center">
              <Users className="h-10 w-10 text-primary mb-5" />
              <h2 className="font-serif text-3xl md:text-4xl font-600 text-foreground leading-tight">
                Angeln beginnt in jungen Jahren
              </h2>
              <p className="mt-4 text-lg text-muted-foreground font-300 leading-relaxed">
                In unserer Jugendgruppe entdecken Kinder und Jugendliche das
                Angeln, lernen den respektvollen Umgang mit der Natur und werden
                Teil einer lebendigen Gemeinschaft.
              </p>
              <Link to="/jugend" className="inline-block mt-7">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-500 gap-2">
                  Zur Jugendgruppe <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT BAND */}
      <section className="py-20 bg-secondary/60 border-t border-border">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h3 className="font-serif text-3xl md:text-4xl font-600 text-foreground">
              Interesse an einer Mitgliedschaft?
            </h3>
            <p className="mt-2 text-muted-foreground text-lg">
              Schreibt uns – wir freuen uns auf euch. Petri Heil!
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/mitglied-werden">
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-600"
              >
                Mitglied werden
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button
                size="lg"
                variant="outline"
                className="w-full border-primary/30 text-primary hover:bg-secondary font-500"
              >
                Zur Kontaktseite
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
